import { SignUp } from '@clerk/react';
import ErrorOutlinedIcon from '@mui/icons-material/ErrorOutlined';
import { Alert, Box, CircularProgress, Container, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useInjection } from 'inversify-react';
import { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { writePendingSignupLinkToken } from '../../hooks/use-signup-link-claim';
import { SignupLinkPublicService } from '../../services/signup-link/signup-link-public.service';
import type {
  ISignupLinkPublicService,
  SignupLinkInvalidReason,
} from '../../services/signup-link/signup-link-public.service.interface';
import { clerkAppearance } from '../../theme/clerk-appearance';
import { AuthLayout } from '../auth/auth-layout';

export function SignupLinkPage() {
  const { t } = useTranslation();
  const { token: urlToken } = useParams<{ token: string }>();

  const frozenToken = useRef(urlToken).current;
  // Congelato al mount come il token: se Clerk ripulisce l'hash a metà
  // transfer, un re-render non deve smontare/rimontare <SignUp> con props
  // diverse (l'account OAuth resterebbe a metà).
  const enteredViaSsoCallback = useRef(
    typeof window !== 'undefined' && window.location.hash.startsWith('#/sso-callback'),
  ).current;
  const service = useInjection<ISignupLinkPublicService>(SignupLinkPublicService);

  const {
    isPending,
    isError,
    error,
    data: info,
  } = useQuery({
    queryKey: ['signup-link', frozenToken],
    queryFn: () =>
      frozenToken
        ? service.getByToken(frozenToken)
        : Promise.reject({ reason: 'notFound' as SignupLinkInvalidReason }),
    enabled: !!frozenToken,
    retry: false,
  });

  const reason: SignupLinkInvalidReason = isError
    ? ((error as { reason?: SignupLinkInvalidReason }).reason ?? 'notFound')
    : 'notFound';

  // Il token RESTA nell'URL: un refresh (o una ri-condivisione del link) deve
  // ripresentare banner e attribuzione, non degradare al bare path. Il Referer
  // non è un canale di fuga: i browser supportati girano col default
  // strict-origin-when-cross-origin, che cross-origin non invia mai path né
  // query — le richieste asset di Clerk non portano il token.
  useLayoutEffect(() => {
    if (info && frozenToken) {
      // Persist the token so the post-auth claim can recover it even if Clerk's
      // email-code step restarts the flow and drops SignUp.unsafeMetadata.
      writePendingSignupLinkToken(frozenToken);
    }
  }, [info, frozenToken]);

  // Bare /signup-link (no :token) oppure rientro OAuth su /signup-link/:token#/sso-callback.
  // Clerk's hash routing returns to the pre-redirect path, so <SignUp> must mount to
  // process the hash and complete the transfer REGARDLESS of the public lookup above
  // (retry:false): un blip di rete, un 5xx o il throttler non devono orfanare un
  // account Clerk creato a metà. The token persists in sessionStorage from the
  // original landing and is recovered post-auth by useSignupLinkClaim — no banner needed.
  if (!frozenToken || enteredViaSsoCallback) {
    return (
      <AuthLayout>
        <SignUp appearance={clerkAppearance} forceRedirectUrl="/" />
      </AuthLayout>
    );
  }

  if (isPending) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress />
          <Typography>{t('signupLink.loading')}</Typography>
        </Box>
      </Container>
    );
  }

  if (isError) {
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <ErrorOutlinedIcon sx={{ fontSize: 56, color: 'error.main' }} />
          <Typography variant="h6" sx={{ textAlign: 'center' }}>
            {t(`signupLink.invalid.${reason}`)}
          </Typography>
          <Link to="/sign-up" style={{ textDecoration: 'none' }}>
            <Typography color="primary" sx={{ fontWeight: 600 }}>
              {t('signupLink.fallback')}
            </Typography>
          </Link>
        </Box>
      </Container>
    );
  }

  const trialBanner = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 400 }}>
      <Alert severity="info" icon={false} sx={{ width: '100%', borderRadius: 2 }}>
        <Typography sx={{ fontWeight: 600, textAlign: 'center' }}>
          {t('signupLink.trialInfo', { count: info.trialDays })}
        </Typography>
        {info.consultantName != null && (
          <>
            <Typography
              data-testid="signup-link-consultant-line"
              sx={{ fontWeight: 500, textAlign: 'center', mt: 0.5 }}
            >
              {t('signupLink.invitedBy', { name: info.consultantName })}
            </Typography>
            {/* L'iscrizione dal link apre subito il workspace al consulente:
                dirlo qui è ciò che rende il consenso esplicito, invece di far
                scoprire l'accesso dopo, dalla lista del team. */}
            <Typography
              data-testid="signup-link-consultant-access-notice"
              variant="caption"
              sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
            >
              {t('signupLink.consultantAccessNotice', { name: info.consultantName })}
            </Typography>
          </>
        )}
        {info.discountPercentOff != null && (
          <Typography
            data-testid="signup-link-discount-line"
            sx={{ fontWeight: 600, textAlign: 'center', mt: 0.5, color: 'secondary.dark' }}
          >
            {info.annualOnly
              ? t('signupLink.discountInfoAnnual', { percent: info.discountPercentOff })
              : t('signupLink.discountInfoMonths', {
                  percent: info.discountPercentOff,
                  months: info.discountDurationInMonths,
                })}
          </Typography>
        )}
      </Alert>
      {info.discountUnavailable && (
        <Alert
          data-testid="signup-link-discount-unavailable"
          severity="warning"
          icon={false}
          sx={{ width: '100%', borderRadius: 2 }}
        >
          <Typography sx={{ fontWeight: 500, textAlign: 'center', fontSize: '0.85rem' }}>
            {t('signupLink.discountUnavailable')}
          </Typography>
        </Alert>
      )}
    </Box>
  );

  return (
    <AuthLayout banner={trialBanner}>
      <SignUp
        appearance={clerkAppearance}
        unsafeMetadata={{ signupLinkToken: frozenToken }}
        forceRedirectUrl="/"
      />
    </AuthLayout>
  );
}
